<script setup lang="ts">
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import StatusBadge from '@/components/common/StatusBadge.vue'
import RcCustomerSelect from '@/components/rc/common/RcCustomerSelect.vue'
import ChatDialog from '@/components/rc/work-orders/ChatDialog.vue'
import { NotifyFail, NotifySuccess } from '@/helpers/core/notifications'
import { dateFormatter } from '@/helpers/ep/formatters'
import { getWorkOrders, getWorkOrdersExportCSV, updateWorkOrder } from '@/services/rc/work-orders.service'
import { useOIDCStore } from '@/stores/oidc.store'
import type { ICustomer } from '@/types/core/Customer'
import type { IWorkOrderFilters } from '@/types/rc/filters'
import type { IWorkOrderUpdateForm } from '@/types/rc/forms'
import { RWOOwnership } from '@/types/rc/filters'
import { WorkOrderPriority } from '@/types/rc/work-orders/WorkOrderPriority'
import { WorkOrderStatus } from '@/types/rc/work-orders/WorkOrderStatus'
import { OrderBy } from '@/types/shared/filters'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { downloadFile, hex2rgba, getCssVarValue } from '@/util'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

const { isCustomer, isRcAppManagerUser } = useOIDCStore()
const queryClient = useQueryClient()

const isChatDialogVisible = ref<boolean>(false)
const workOrderChatNumber = ref<string>()

// Priority editing state
const editingPriority = ref<Record<string, boolean>>({})
const selectedPriority = ref<Record<string, WorkOrderPriority | undefined>>({})

// Priority color mapping similar to ClaimStatusTag
const priorityColorMap: Record<string, string> = {
  [WorkOrderPriority.Low]: 'var(--priority-low)',
  [WorkOrderPriority.Normal]: 'var(--priority-normal)',
  [WorkOrderPriority.High]: 'var(--priority-high)',
  [WorkOrderPriority.Urgent]: 'var(--priority-urgent)',
}

// Helper functions for priority display
const getPriorityColor = (priority: string): string => {
  return priorityColorMap[priority] || 'var(--status-default)'
}

// Filters
const workOrderStatusFilterOptions = Object.values(WorkOrderStatus).map((v) => ({ key: v, value: v }))
const workOrderPriorityFilterOptions = Object.values(WorkOrderPriority).map((v) => ({ key: v, value: v }))
const workSourceFilterOptions = [
  {
    key: RWOOwnership.OUR_WORK,
    value: 'Our work',
  },
  {
    key: RWOOwnership.OUTSOURCED,
    value: 'Outsourced',
  },
]
const workOrderTypeOptions = [
  {
    key: 'Repair Service Request',
    value: 'Repair Service Request',
  },
  {
    key: 'Return Merchandise Authorization',
    value: 'Return Merchandise Authorization',
  },
  {
    key: 'ASIC Warranty Claim',
    value: 'ASIC Warranty Claim',
  },
]

const statusFilter = ref<string>()
const priorityFilter = ref<string>()
const workSourceFilter = ref<RWOOwnership>()
const workOrderTypeFilter = ref<string>()
const baseFilterSettings = [
  {
    key: 'status',
    value: 'Status',
    options: workOrderStatusFilterOptions,
    type: 'dropdown',
    placeholder: 'Status',
    modelValue: statusFilter,
    onChange: (val: string) => {
      filters.status = val
    },
    style: 'width: 232px',
    iconClass: 'label-outline-rounded',
  },
  {
    key: 'type',
    value: 'Type',
    options: workOrderTypeOptions,
    type: 'dropdown',
    placeholder: 'Type',
    modelValue: workOrderTypeFilter,
    onChange: (val: string) => {
      filters.type = val
    },
    style: 'width: 232px',
    iconClass: 'package-2-outline',
  },
]
const filterSettings = isCustomer
  ? baseFilterSettings
  : [
      ...baseFilterSettings,
      {
        key: 'priority',
        value: 'Priority',
        options: workOrderPriorityFilterOptions,
        type: 'dropdown',
        placeholder: 'Priority',
        modelValue: priorityFilter,
        onChange: (val: string) => {
          filters.priority = val
        },
        style: 'width: 232px',
        iconClass: 'priority-high',
      },
      {
        key: 'work_source',
        value: 'Work Source',
        options: workSourceFilterOptions,
        type: 'dropdown',
        placeholder: 'Work Source',
        modelValue: workSourceFilter,
        onChange: (val: RWOOwnership) => {
          filters.owner = val
        },
        style: 'width: 232px',
        iconClass: 'person-outline',
      },
    ]

const searchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Work Order #',
  style: 'width: 232px;',
  onChange: (val: string) => {
    if (val == '') {
      filters.number = undefined
    } else {
      filters.number = val
    }
  },
}

const router = useRouter()
const filters = reactive<IWorkOrderFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'updated_at',
  order_by_order: OrderBy.DESC,
  customer_id: undefined,
  status: undefined,
  number: undefined,
  owner: undefined,
  type: undefined,
  has_unread_messages: undefined,
  priority: undefined,
})

const isLoadingExport = ref<boolean>(false)

const { isFetching, isError, data, error, isRefetching, refetch } = useQuery({
  queryKey: ['work-orders', filters],
  queryFn: () => getWorkOrders(filters),
  placeholderData: (prev) => prev,
})

// Update work order mutation
const updateWorkOrderMutation = useMutation({
  mutationFn: ({ workOrderNumber, form }: { workOrderNumber: string; form: IWorkOrderUpdateForm }) =>
    updateWorkOrder(workOrderNumber, form),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['work-orders'] })
    NotifySuccess('Work Order updated successfully.')
  },
  onError: (e: any) => NotifyFail(e.message),
})

const navigateToWorkOrderDashboard = async (number: string) => {
  router.push({ name: 'rc-work-order-dashboard', params: { number } })
}

const handleCustomerSelectChange = (val: ICustomer | undefined) => {
  filters.customer_id = val?.id
}

const handleChatDialogOpen = (workOrderNumber: string) => {
  workOrderChatNumber.value = workOrderNumber
  isChatDialogVisible.value = true
}

const handleChatDialogClose = () => {
  isChatDialogVisible.value = false
  workOrderChatNumber.value = undefined
}

const handleExport = async () => {
  isLoadingExport.value = true
  try {
    const result = await getWorkOrdersExportCSV(filters)
    downloadFile(result, 'work_orders_export.csv')
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Unknown error occurred'
    NotifyFail(message)
  } finally {
    isLoadingExport.value = false
  }
}

const handleRowClick = (row: any, col: { type: string } | null) => {
  if (!col) return
  if (col.type != 'notExpandable') navigateToWorkOrderDashboard(row.number)
}

// Priority editing handlers
const startEditingPriority = (workOrderNumber: string, currentPriority?: WorkOrderPriority) => {
  editingPriority.value[workOrderNumber] = true
  selectedPriority.value[workOrderNumber] = currentPriority
}

const cancelEditingPriority = (workOrderNumber: string) => {
  editingPriority.value[workOrderNumber] = false
  selectedPriority.value[workOrderNumber] = undefined
}

const onPriorityChange = (workOrderNumber: string, priorityValue?: WorkOrderPriority) => {
  const form: IWorkOrderUpdateForm = { priority: priorityValue ?? null }
  updateWorkOrderMutation.mutate(
    { workOrderNumber, form },
    {
      onSettled: () => {
        editingPriority.value[workOrderNumber] = false
      },
    }
  )
}
</script>

<template>
  <ViewLayout>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="inline-flex justify-between">
        <div class="flex gap-spacing-sm flex-wrap">
          <TableSearchFilter :search-settings="searchSettings" :disabled="isFetching" />
          <RcCustomerSelect
            v-if="!isCustomer"
            style="width: 232px"
            :disabled="isFetching"
            @change="handleCustomerSelectChange"
          />
          <TableFilters :filters-list="filterSettings" />
          <el-checkbox v-model="filters.has_unread_messages" label="New Messages" />
        </div>
        <el-button :loading="isLoadingExport" @click="handleExport">Export</el-button>
      </div>
      <el-table
        v-loading="isFetching"
        :data="data?.data"
        :stripe="true"
        row-class-name="cursor-pointer"
        @row-click="handleRowClick"
      >
        <el-table-column min-width="352" prop="number" label="Work Order #" />
        <el-table-column min-width="112" prop="miners_quantity" label="Miner Quantity">
          <template #default="scope">
            <el-tooltip effect="light" placement="bottom">
              <template #content>
                <p>{{ scope.row.inventoried_units }} inventoried</p>
                <p>{{ scope.row.expected_units }} expected</p>
              </template>
              <el-button link>{{ scope.row.inventoried_units }}/{{ scope.row.expected_units }}</el-button>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column min-width="112" prop="outsourced" label="Outsourced">
          <template #default="scope">
            <el-checkbox v-model="scope.row.outsourced" disabled />
          </template>
        </el-table-column>
        <el-table-column v-if="!isCustomer" min-width="192" prop="customer" label="Customer" />
        <el-table-column min-width="112" prop="start_date" label="Start Date" :formatter="dateFormatter" />
        <el-table-column min-width="112" prop="due_date" label="Due Date" :formatter="dateFormatter" />
        <el-table-column min-width="112" label="Status">
          <template #default="scope">
            <StatusBadge :status="scope.row.status" />
          </template>
        </el-table-column>
        <el-table-column v-if="!isCustomer" min-width="140" prop="priority" label="Priority" align="center">
          <template #default="scope">
            <!-- Displaying -->
            <div v-if="!editingPriority[scope.row.number]" class="flex gap-2 items-center justify-center">
              <el-tag
                v-if="scope.row.priority"
                disable-transitions
                :color="`${hex2rgba(getCssVarValue(getPriorityColor(scope.row.priority)), 0.2)}`"
                class="inline-flex items-center gap-1 cursor-pointer"
                style="border: none"
                @click="
                  (event: any) => isRcAppManagerUser && startEditingPriority(scope.row.number, scope.row.priority)
                "
              >
                <span :style="{ color: getPriorityColor(scope.row.priority) }">
                  {{ scope.row.priority }}
                </span>
                <Icon
                  v-if="isRcAppManagerUser"
                  :color="getPriorityColor(scope.row.priority)"
                  icon="material-symbols:keyboard-arrow-down-rounded"
                  :height="16"
                />
              </el-tag>
              <el-tag
                v-else
                disable-transitions
                color="var(--el-fill-color-lighter)"
                class="inline-flex items-center gap-1 cursor-pointer"
                style="border: 1px dashed var(--el-border-color); color: var(--el-text-color-placeholder)"
                @click="
                  (event: any) => isRcAppManagerUser && startEditingPriority(scope.row.number, scope.row.priority)
                "
              >
                <span>Priority</span>
                <Icon
                  v-if="isRcAppManagerUser"
                  color="var(--el-text-color-placeholder)"
                  icon="material-symbols:keyboard-arrow-down-rounded"
                  :height="16"
                />
              </el-tag>
            </div>
            <!-- Editing -->
            <span v-else class="text-nowrap flex gap-2 items-center justify-center">
              <el-select
                :model-value="selectedPriority[scope.row.number]"
                min-width="120"
                :disabled="updateWorkOrderMutation.isPending.value"
                :loading="updateWorkOrderMutation.isPending.value"
                clearable
                @click.stop
                @change="(val) => onPriorityChange(scope.row.number, val)"
              >
                <el-option v-for="opt in Object.values(WorkOrderPriority)" :key="opt" :label="opt" :value="opt" />
              </el-select>

              <el-button
                :disabled="updateWorkOrderMutation.isPending.value"
                link
                @click.stop="cancelEditingPriority(scope.row.number)"
              >
                <Icon icon="material-symbols:close" :height="20" />
              </el-button>
            </span>
          </template>
        </el-table-column>
        <el-table-column
          v-if="isRcAppManagerUser || isCustomer"
          min-width="50"
          type="notExpandable"
          @click.stop.prevent
        >
          <template #default="scope">
            <el-badge class="item" :is-dot="scope.row.has_unread_messages" :offset="[0, 5]">
              <el-button link @click="handleChatDialogOpen(scope.row.number)">
                <Icon icon="material-symbols:mail-outline" :height="20" />
              </el-button>
            </el-badge>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data"
        v-model:current-page="filters.page"
        v-model:page-size="filters.per_page"
        class="self-end"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
      <ChatDialog
        v-model="isChatDialogVisible"
        :work-order-number="workOrderChatNumber"
        @closed="handleChatDialogClose"
      />
    </template>
  </ViewLayout>
</template>

<style scoped lang="scss">
:deep(.el-tag__content) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>
